/**
 * @file EmployeeSpeakerResolvePanel.tsx
 * @brief Owner-side speaker tagging via `/api/boardroom/.../resolve`.
 *
 * @author DES
 * @date 28-07-2026
 * @updated 25-09-2026 — flush grid cells, same tracks as the admin resolve panel
 * @updated 02-10-2026 — a people picker per speaker, as mobile Meeting Tagging
 *   (DES: "on speaker resolution it doesn't give me a picker like I have on
 *   mobile, it only allows me to add names"). The list is this meeting's
 *   participants, you, and the company roster; its search also adds an
 *   external person by name or email (ExternalFromSearch + the account
 *   lookup), so a quick meeting with no invitees can still be resolved.
 * @updated 22-10-2026 — `mode="notes"`: "Name speakers" on a ready Meeting
 *   Notes meeting (BACKEND 00a84bd). Same picker and the same resolve route;
 *   the owner's own voice comes named, the rest are named here, and the notes
 *   are written again with the names. No auto-compile, no "all named" rule.
 * @updated 28-10-2026 — W30 "last one left" (B37): when one voice and one
 *   person on this meeting are left, that person shows in muted text with a
 *   "Last one left" chip. Never applied: the chip (or the picker, where they
 *   are listed first as "Suggested") fills the row like a hand pick. Owner
 *   only; the rule follows the form as names are picked (lastOneLeft.ts).
 */

import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { ChevronDown, LoaderCircle, Mic, Sparkles, Volume2 } from "lucide-react";
import { Avatar } from "@/components/ui/Avatar";
import { Button } from "@/components/ui/Button";
import { PickerSheet, type PickerSheetItem } from "@/components/ui/PickerSheet";
import { AudioPlayBadge } from "@/components/ui/AudioPlayer";
import { ApiClientError, apiGet } from "@/shared/api/client";
import { companyEndpoints } from "@/shared/api/endpoints";
import { useAuth } from "@/shared/auth/auth-context";
import { getInitials } from "@/shared/utils/formatters";
import { toast } from "@/shared/hooks/useToast";
import { cn } from "@/shared/theme/cn";
import {
  GridBand,
  GridCell,
  GridCells,
} from "@/features/analytics/components/GridBlock";
import { companyMeetingsKeys } from "@/features/company-meetings/api";
import type { MeetingDetail } from "@/features/company-meetings/schemas";
import {
  autoSynthesizeBoardroom,
  resolveBoardroomSpeakers,
} from "./captureApi";
import { ExternalFromSearch } from "./ExternalFromSearch";
import {
  pickSuggestion,
  serverSuggestion,
  type LastOneLeft,
} from "./lastOneLeft";
import {
  FULL_EMAIL_RE,
  hasExternalBlock,
  useGuestLookup,
} from "./participantSearch";

type SpeakerDraft = {
  speakerId: string;
  voiceClipUrl: string | null;
  name: string;
  isExternal: boolean;
  email: string;
  profileImageUrl: string | null;
  /** Named by the backend voiceprint match (DES 10-10-2026: shown as "Auto",
   *  as the app's sparkle). A manual pick clears it. */
  autoMatched: boolean;
  /** The account behind the name, when known; sent on resolve. */
  userId: string | null;
  /** Sounds like nobody invited (diarization): never suggested a name. */
  unknownVoice: boolean;
};

/** Someone a voice can be given to. */
type Person = {
  key: string;
  name: string;
  email: string;
  isExternal: boolean;
  profileImageUrl: string | null;
  userId?: string | null;
};

type PersonItem = PickerSheetItem & { person: Person };

type CompanyUserRow = {
  id: string;
  first_name?: string | null;
  last_name?: string | null;
  email: string;
  status?: string;
  profile_image_url?: string | null;
};

const personKey = (name: string, email: string) =>
  email ? `e:${email.toLowerCase()}` : `n:${name.trim().toLowerCase()}`;

/** A track's placeholder label ("Speaker 2"), not a person's name. */
const PLACEHOLDER_NAME_RE = /^speaker\s*\d+$/i;

function seedDrafts(meeting: MeetingDetail): SpeakerDraft[] {
  return (meeting.resolved_speakers ?? [])
    .map((s) => {
      const speakerId = s.speakerId ?? s.speaker_id ?? "";
      if (!speakerId) return null;
      const resolved = (s.resolvedUser ?? null) as
        | (NonNullable<typeof s.resolvedUser> & {
            email?: string | null;
            userId?: string | null;
          })
        | null;
      const name = (resolved?.name ?? s.name ?? "").trim();
      return {
        speakerId,
        voiceClipUrl: s.voiceClipUrl ?? null,
        name: PLACEHOLDER_NAME_RE.test(name) ? "" : name,
        isExternal: Boolean(s.isExternal ?? s.is_external ?? resolved?.isExternal),
        email: (resolved?.email ?? "").trim(),
        profileImageUrl:
          resolved?.profileImageUrl ?? s.profileImageUrl ?? s.profile_image_url ?? null,
        autoMatched: Boolean((resolved as { autoMatched?: boolean } | null)?.autoMatched),
        userId: resolved?.userId ?? null,
        unknownVoice: s.unknownVoice === true,
      };
    })
    .filter((d): d is SpeakerDraft => d !== null);
}

function ResolveCell({
  header = false,
  className,
  children,
}: {
  header?: boolean;
  className?: string;
  children: ReactNode;
}) {
  return (
    <GridCell
      padded={false}
      className={cn(
        "flex min-w-0 items-center px-3 py-2.5",
        header &&
          "sticky top-0 z-10 hidden bg-muted py-2 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground sm:flex",
        className,
      )}
    >
      {children}
    </GridCell>
  );
}

export function EmployeeSpeakerResolvePanel({
  meeting,
  mode = "resolve",
  onDone,
}: {
  meeting: MeetingDetail;
  /** `notes`: name the speakers of a ready Meeting Notes meeting. */
  mode?: "resolve" | "notes";
  /** Notes mode: close the panel (Cancel, or after the names are saved). */
  onDone?: () => void;
}) {
  const isNotes = mode === "notes";
  const [drafts, setDrafts] = useState(() => seedDrafts(meeting));
  const [seeded] = useState(drafts);
  const qc = useQueryClient();
  const { user } = useAuth();

  // One picker open at a time: its speaker, search text and guest name.
  const [pickingFor, setPickingFor] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [guestName, setGuestName] = useState("");
  const lookup = useGuestLookup(search);
  // People added from the search, kept for the other speakers' pickers.
  const [added, setAdded] = useState<Person[]>([]);

  // The same roster query as Boardroom setup, so it is usually cached.
  const rosterQuery = useQuery({
    queryKey: ["company", "users", "boardroom-roster"],
    queryFn: async () => {
      const raw = await apiGet<{ users?: CompanyUserRow[] }>(companyEndpoints.users);
      return (raw.users ?? []).filter((u) => u.status !== "suspended");
    },
  });

  /** This meeting's people first, then you, the company, and anyone added;
   *  each once (by email, else by name). */
  const people = useMemo<Person[]>(() => {
    const out: Person[] = [];
    const seen = new Set<string>();
    const push = (p: Omit<Person, "key">) => {
      const name = p.name.trim();
      if (!name) return;
      const key = personKey(name, p.email);
      if (seen.has(key)) return;
      seen.add(key);
      out.push({ ...p, name, key });
    };
    for (const p of meeting.participants ?? []) {
      const roster = p as { userId?: string | null; user_id?: string | null };
      push({
        name: p.name ?? "",
        email: (p.email ?? "").trim(),
        isExternal: !!p.isExternal,
        profileImageUrl: p.profileImageUrl ?? null,
        userId: roster.userId ?? roster.user_id ?? null,
      });
    }
    if (user) {
      push({
        name:
          [user.firstName, user.lastName].filter(Boolean).join(" ").trim() ||
          user.name ||
          user.email,
        email: user.email,
        isExternal: false,
        profileImageUrl: user.profileImageUrl ?? null,
      });
    }
    for (const u of rosterQuery.data ?? []) {
      push({
        name: [u.first_name, u.last_name].filter(Boolean).join(" ").trim() || u.email,
        email: u.email,
        isExternal: false,
        profileImageUrl: u.profile_image_url ?? null,
      });
    }
    for (const p of added) push(p);
    return out;
  }, [meeting.participants, user, rosterQuery.data, added]);

  /** Who started the meeting: the owner, found in the roster saved with it,
   *  else the signed-in user when that is the owner, else the company list.
   *  The panel also runs for a company admin on someone else's meeting, so
   *  the signed-in user is not assumed to be the host. */
  const host = useMemo<Person | null>(() => {
    const ownerId = meeting.owner_id ?? null;
    if (!ownerId) return null;
    const fromRoster = (meeting.participants ?? []).find((p) => p.id === ownerId);
    let email = fromRoster?.email ?? null;
    if (!email && user?.id === ownerId) email = user.email;
    if (!email) {
      email = rosterQuery.data?.find((u) => u.id === ownerId)?.email ?? null;
    }
    if (!email) return null;
    const lower = email.toLowerCase();
    return people.find((p) => p.email.toLowerCase() === lower) ?? null;
  }, [meeting.owner_id, meeting.participants, user, rosterQuery.data, people]);

  // One voice in the whole meeting: it is the host's (DES 02-10-2026: "awaz
  // na bhi ho, but start to uskay login say hoi hai"). Filled once, only
  // while that track has no name; the picker can still change it. With
  // several voices and no voiceprint, which one is the host is not known,
  // so nothing is guessed: the host is just first in the list.
  const hostFilled = useRef(false);
  useEffect(() => {
    if (hostFilled.current || !host) return;
    if (drafts.length !== 1 || drafts[0]!.name.trim()) return;
    hostFilled.current = true;
    assign(drafts[0]!.speakerId, host);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [host, drafts]);

  // Last one left (B37): the server's value while the form is as it opened,
  // then the same rule on the form. Owner only.
  const fromServer = useMemo<LastOneLeft | null>(() => {
    for (const s of meeting.resolved_speakers ?? []) {
      const person = serverSuggestion(s);
      const speakerId = s.speakerId ?? s.speaker_id ?? "";
      if (person && speakerId) return { speakerId, person };
    }
    return null;
  }, [meeting.resolved_speakers]);
  const suggestion = useMemo(
    () =>
      pickSuggestion({
        isOwner: meeting.is_owner === true,
        rows: drafts,
        roster: meeting.participants,
        untouched: drafts.every((d) => {
          const before = seeded.find((s) => s.speakerId === d.speakerId);
          return !!before && before.name === d.name && before.email === d.email;
        }),
        fromServer,
      }),
    [meeting.is_owner, meeting.participants, drafts, seeded, fromServer],
  );
  const suggestedKey = suggestion
    ? personKey(suggestion.person.name, suggestion.person.email)
    : null;

  // The picker's search is controlled (it also drives the account lookup),
  // so the list is filtered here, by name or email. The host comes first;
  // on the suggested voice, the suggested person comes first.
  const items = useMemo<PersonItem[]>(() => {
    const q = search.trim().toLowerCase();
    const suggestedHere =
      suggestion && pickingFor === suggestion.speakerId
        ? (people.find((p) => p.key === suggestedKey) ?? null)
        : null;
    const first = [suggestedHere, host].filter(
      (p, i, all): p is Person => !!p && all.findIndex((x) => x?.key === p.key) === i,
    );
    const ordered = [
      ...first,
      ...people.filter((p) => !first.some((f) => f.key === p.key)),
    ];
    return ordered
      .filter(
        (p) => !q || p.name.toLowerCase().includes(q) || p.email.toLowerCase().includes(q),
      )
      .map((person) => ({
        id: person.key,
        label: person.name,
        subtitle:
          person.key === suggestedKey &&
          suggestion &&
          pickingFor === suggestion.speakerId
            ? `Suggested · last one left${person.email ? ` · ${person.email}` : ""}`
            : person.key === host?.key
              ? `Started this meeting · ${person.email}`
              : person.email || "External participant",
        person,
      }));
  }, [people, search, host, suggestion, suggestedKey, pickingFor]);

  const assign = (speakerId: string, person: Omit<Person, "key">) => {
    setDrafts((prev) =>
      prev.map((row) =>
        row.speakerId === speakerId
          ? {
              ...row,
              name: person.name,
              email: person.email,
              isExternal: person.isExternal,
              profileImageUrl: person.profileImageUrl,
              autoMatched: false,
              userId: person.userId ?? null,
            }
          : row,
      ),
    );
  };

  const closePicker = () => {
    setPickingFor(null);
    setSearch("");
    setGuestName("");
  };

  /** From the search (ExternalFromSearch): a new person for this voice. A
   *  colleague found by email is company, not external. */
  const addFromSearch = ({ name, email }: { name: string; email: string }) => {
    if (!pickingFor) return;
    const found = !!email && lookup.status === "found";
    const colleague =
      !!email && lookup.status === "inlist" && lookup.reason === "company";
    const person: Person = {
      key: personKey(name, email),
      name: name.trim(),
      email: email.trim(),
      isExternal: !colleague,
      profileImageUrl:
        found && lookup.status === "found" ? (lookup.profileImageUrl ?? null) : null,
    };
    setAdded((prev) => [...prev, person]);
    assign(pickingFor, person);
    closePicker();
  };

  const typed = search.trim().toLowerCase();
  const alreadyListed =
    FULL_EMAIL_RE.test(typed) && people.some((p) => p.email.toLowerCase() === typed);

  const resolve = useMutation({
    mutationFn: async () => {
      // Notes: only the voices changed here, so the owner's auto-named
      // voice keeps its "Auto" match.
      const payload = (isNotes ? changed : drafts).map((d) => ({
        speakerId: d.speakerId,
        name: d.name.trim(),
        isExternal: d.isExternal,
        // As mobile Meeting Tagging sends them (the server matches accounts
        // by email).
        email: d.email || undefined,
        profileImageUrl: d.profileImageUrl,
        ...(d.userId ? { userId: d.userId } : {}),
      }));
      return resolveBoardroomSpeakers(meeting.id, payload);
    },
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: companyMeetingsKeys.detail(meeting.id),
      });
      void qc.invalidateQueries({ queryKey: companyMeetingsKeys.all });
    },
  });

  const autoCompile = useMutation({
    mutationFn: () => autoSynthesizeBoardroom(meeting.id),
    onSuccess: () => {
      void qc.invalidateQueries({
        queryKey: companyMeetingsKeys.detail(meeting.id),
      });
      void qc.invalidateQueries({ queryKey: companyMeetingsKeys.all });
    },
  });

  const allNamed = useMemo(
    () => drafts.length > 0 && drafts.every((d) => d.name.trim().length > 0),
    [drafts],
  );
  const changed = useMemo(
    () =>
      drafts.filter((d) => {
        const before = seeded.find((s) => s.speakerId === d.speakerId);
        return !before || before.name !== d.name || before.email !== d.email;
      }),
    [drafts, seeded],
  );

  const speakerCount = meeting.resolved_speakers?.length ?? 0;
  const participantCount = meeting.participants?.length ?? 0;
  const canAuto = speakerCount > 0 && speakerCount === participantCount;

  if (drafts.length === 0) return null;

  const busy = resolve.isPending || autoCompile.isPending;

  return (
    <>
      <GridBand className="bg-warning-soft/40">
        <h3 className="flex items-center gap-1.5 text-sm font-semibold">
          <Mic className="h-4 w-4 text-warning" />
          {isNotes ? "Name speakers" : "Resolve speakers"}
        </h3>
        <p className="mt-1 text-xs text-muted-foreground">
          {isNotes
            ? "Your own voice is already named. Pick who the other voices are, then save: the notes are written again with the names."
            : "Name each voice track, then compile intelligence — same step as mobile Meeting Tagging."}
        </p>
      </GridBand>

      <GridCells className="max-h-[min(60vh,28rem)] grid-cols-1 overflow-y-auto sm:grid-cols-[max-content_max-content_minmax(0,1fr)]">
        <ResolveCell header>Speaker</ResolveCell>
        <ResolveCell header>Voice</ResolveCell>
        <ResolveCell header>Name</ResolveCell>
        {drafts.flatMap((d, idx) => {
          const offer =
            !d.name && suggestion?.speakerId === d.speakerId ? suggestion.person : null;
          return [
          <ResolveCell
            key={`${d.speakerId}-speaker`}
            className="bg-muted/30 sm:bg-transparent"
          >
            <span className="text-xs font-semibold">Speaker {idx + 1}</span>
          </ResolveCell>,
          <ResolveCell key={`${d.speakerId}-voice`}>
            <AudioPlayBadge
              src={d.voiceClipUrl}
              variant="original"
              label="Play"
              icon={<Volume2 className="h-3 w-3" />}
              className="h-8 w-fit px-2.5 text-[10px]"
            />
          </ResolveCell>,
          <ResolveCell key={`${d.speakerId}-name`}>
            <div className="relative w-full min-w-0">
            <PickerSheet<PersonItem>
              anchor={
                <button
                  type="button"
                  disabled={busy}
                  aria-label={`Name for Speaker ${idx + 1}`}
                  className="flex h-9 w-full min-w-0 items-center gap-2 rounded-full border border-input bg-field pl-1 pr-3 text-left text-sm transition-colors hover:bg-secondary disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {d.name ? (
                    <Avatar
                      src={d.profileImageUrl}
                      alt={d.name}
                      initials={getInitials(d.name)}
                      size="sm"
                    />
                  ) : (
                    <span className="h-7 w-1" aria-hidden />
                  )}
                  <span
                    className={cn(
                      "min-w-0 flex-1 truncate",
                      d.name ? "font-medium text-foreground" : "text-muted-foreground",
                    )}
                  >
                    {/* The signed-in user's own voice reads "You" (DES 10-10-2026).
                        A suggestion is muted until the owner accepts it. */}
                    {d.name
                      ? user?.email && d.email.toLowerCase() === user.email.toLowerCase()
                        ? "You"
                        : d.name
                      : (offer?.name ?? "Pick who this is")}
                  </span>
                  {d.autoMatched ? (
                    <span
                      className="inline-flex shrink-0 items-center gap-1 rounded-full bg-brand/10 px-1.5 py-0.5 text-[10px] font-semibold text-brand"
                      title="Matched by voice"
                    >
                      <Sparkles className="h-3 w-3" aria-hidden />
                      Auto
                    </span>
                  ) : null}
                  {/* Room for the "Last one left" chip, which sits on top. */}
                  {offer ? <span className="w-[6.75rem] shrink-0" aria-hidden /> : null}
                  <ChevronDown className="h-4 w-4 shrink-0 text-muted-foreground" aria-hidden />
                </button>
              }
              open={pickingFor === d.speakerId}
              onOpenChange={(open) => {
                if (open) {
                  setPickingFor(d.speakerId);
                  setSearch("");
                  setGuestName("");
                } else if (pickingFor === d.speakerId) {
                  closePicker();
                }
              }}
              title={`Speaker ${idx + 1}`}
              subtitle="Who is this voice? Search the meeting and your company, or type a name or email to add someone from outside."
              searchPlaceholder="Name or email…"
              searchValue={pickingFor === d.speakerId ? search : ""}
              onSearchChange={setSearch}
              items={items}
              selectedId={d.name ? personKey(d.name, d.email) : null}
              onSelect={(item) => {
                assign(d.speakerId, item.person);
                closePicker();
              }}
              clearable={false}
              // The meeting's own people are there at once; wait only when
              // there is no one to show yet.
              loading={rosterQuery.isLoading && people.length === 0}
              listHeader={
                pickingFor === d.speakerId && hasExternalBlock(search) ? (
                  <ExternalFromSearch
                    query={search}
                    lookup={lookup}
                    guestName={guestName}
                    setGuestName={setGuestName}
                    blockedMessage={alreadyListed ? "Already in the list below." : null}
                    onAdd={addFromSearch}
                  />
                ) : null
              }
              renderLeftAccessory={(item) => (
                <Avatar
                  src={item.person.profileImageUrl}
                  alt={item.label}
                  initials={getInitials(item.label)}
                  size="xl"
                />
              )}
              renderTrailingAccessory={(item) =>
                item.person.isExternal ? (
                  <span className="text-[10px] font-semibold uppercase tracking-wide text-amber-700">
                    External
                  </span>
                ) : null
              }
              emptyText="No one matches. Type a name or email above to add them."
            />
            {offer ? (
              <button
                type="button"
                disabled={busy}
                onClick={() =>
                  assign(d.speakerId, {
                    name: offer.name,
                    email: offer.email,
                    isExternal: offer.isExternal,
                    profileImageUrl: offer.profileImageUrl,
                    userId: offer.userId,
                  })
                }
                title="The only person on this meeting not yet matched to a voice"
                aria-label={`Suggested: ${offer.name}, last one left. Use this name.`}
                className="absolute right-9 top-1/2 inline-flex -translate-y-1/2 items-center gap-1 rounded-full border border-brand/30 bg-brand/10 px-2 py-0.5 text-[10px] font-semibold text-brand transition-colors hover:bg-brand/20 disabled:cursor-not-allowed disabled:opacity-50"
              >
                Last one left
                <span className="font-bold underline underline-offset-2">Use</span>
              </button>
            ) : null}
            </div>
          </ResolveCell>,
          ];
        })}
      </GridCells>

      {isNotes ? (
        <GridBand className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            disabled={busy || changed.length === 0}
            onClick={() => {
              void resolve.mutateAsync().then(
                () => {
                  toast({
                    variant: "success",
                    title: "Names saved",
                    description: "The notes are being written again with the names.",
                  });
                  onDone?.();
                },
                (err: unknown) =>
                  toast({
                    variant: "destructive",
                    title: "Could not save the names",
                    description:
                      err instanceof ApiClientError && err.code === "MEETING_BUSY"
                        ? "Notes are being written. Try again once they are ready."
                        : err instanceof ApiClientError
                          ? err.message
                          : "Please try again.",
                  }),
              );
            }}
          >
            {resolve.isPending ? (
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Save names
          </Button>
          <Button type="button" variant="outline" disabled={busy} onClick={() => onDone?.()}>
            Cancel
          </Button>
        </GridBand>
      ) : (
      <GridBand className="flex flex-wrap items-center gap-2">
          <Button
            type="button"
            disabled={busy || !allNamed}
            onClick={() => {
              void resolve.mutateAsync().then(
                () =>
                  toast({
                    variant: "success",
                    title: "Intelligence compiled",
                  }),
                (err: unknown) =>
                  toast({
                    variant: "destructive",
                    title: "Compile failed",
                    description:
                      err instanceof ApiClientError
                        ? err.message
                        : "Please try again.",
                  }),
              );
            }}
          >
            {resolve.isPending ? (
              <LoaderCircle className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Sparkles className="mr-2 h-4 w-4" />
            )}
            Compile intelligence
          </Button>
          {canAuto ? (
            <Button
              type="button"
              variant="outline"
              disabled={busy}
              onClick={() => {
                void autoCompile.mutateAsync().then(
                  () =>
                    toast({
                      variant: "success",
                      title: "Auto-compiled",
                    }),
                  (err: unknown) =>
                    toast({
                      variant: "destructive",
                      title: "Auto-compile failed",
                      description:
                        err instanceof ApiClientError
                          ? err.message
                          : "Please try again.",
                    }),
                );
              }}
            >
              Auto-compile (roster match)
            </Button>
          ) : null}
      </GridBand>
      )}
    </>
  );
}
